import "./QuestionContext.css";
import { Button } from "@ui/Button";
import { formatNum } from "@ui/format";
import { MaterialSymbol } from "@ui/MaterialSymbol";
import { Pill } from "@ui/Pill";
import { HStack, Stack } from "@ui/Stack";
import { Tooltip, TooltipTrigger } from "@ui/Tooltip";
import { Text } from "@ui/typography/Text";
import { pluralise } from "@utils/text";
import { useConnectedAccountsStats } from "@utils/useConnectedAccountsStats";
import { For, type JSX, Show } from "solid-js";

export type QuestionContextAnswer = {
	id: string;
	title: string;
	/** Tooltip copy when set (e.g. saved-answer handoff from the answers page). */
	description?: string;
	/** Langchain artifact to attach when starting a new conversation. */
	artifactId?: string;
};

export type QuestionContextFile = {
	id: string;
	name: string;
};

export type QuestionContextItems = {
	answers: QuestionContextAnswer[];
	files: QuestionContextFile[];
};

export type QuestionContextPool = {
	/** Own library, the designated public pool, or any other specified pool. */
	kind: "own" | "public" | "other";
	name?: string;
};

type Props = {
	items: QuestionContextItems;
	onRemoveAnswer: (id: string) => void;
	selectedPool?: QuestionContextPool;
	/** A control at the far end of the row. */
	trailing?: JSX.Element;
};

const isEmpty = (items: QuestionContextItems) =>
	items.answers.length === 0 && items.files.length === 0;

const emptyContextLabel = (
	pool: QuestionContextPool | undefined,
	videoCount: number,
) => {
	if (pool == null) {
		return "";
	}
	if (pool.kind === "own") {
		return (
			<HStack align="center" spacing="small">
				Your entire library
				<Text color="primary" family="mono" size="xsmall" weight="semi-bold">
					({formatNum(videoCount)} {pluralise(videoCount, "video")})
				</Text>
			</HStack>
		);
	}
	if (pool.kind === "public") {
		return "All videos";
	}
	const name = pool.name?.trim() ?? "";
	return name.length > 0 ? `Pool: ${name}` : "";
};

export const QuestionContext = (props: Props) => {
	const empty = () => isEmpty(props.items);
	const { stats } = useConnectedAccountsStats();
	const emptyLabel = () =>
		emptyContextLabel(props.selectedPool, stats()?.processed_post_count ?? 0);

	return (
		<HStack
			class="question-context"
			fullWidth
			align="center"
			spacing="small"
			justify="space-between"
		>
			<Stack direction="row" wrap align="center" spacing="small">
				<HStack align="center" spacing="small">
					<MaterialSymbol
						symbol="stacks"
						color={empty() ? "fg3" : "primary"}
						relativeSize={0.75}
					/>
					<Text as="span" size="xsmall" family="mono" color="fg3">
						Context:
					</Text>
				</HStack>
				<Show
					when={!empty()}
					fallback={
						<Show when={emptyLabel()}>
							<Text as="span" size="small" color="fg3">
								{emptyLabel()}
							</Text>
						</Show>
					}
				>
					<For each={props.items.answers}>
						{(answer) => {
							const tooltipText = () => answer.description?.trim() ?? "";
							return (
								<TooltipTrigger disabled={tooltipText() === ""} placement="top">
									<Pill theme="primary" size="small" border>
										<MaterialSymbol
											symbol="auto_awesome"
											filled
											color="primary"
											relativeSize="smaller"
										/>
										{answer.title}
										<Button
											type="button"
											variant="text"
											symbol="close"
											size="small"
											symbolColor="primary"
											touchTarget="compact"
											aria-label={`Remove ${answer.title}`}
											style={{
												padding: 0,
												height: "auto",
												width: "auto",
												"min-height": 0,
												"min-width": 0,
											}}
											onMouseDown={(event) => event.preventDefault()}
											onClick={() => props.onRemoveAnswer(answer.id)}
										/>
									</Pill>
									<Tooltip width="wide">{tooltipText()}</Tooltip>
								</TooltipTrigger>
							);
						}}
					</For>
				</Show>
			</Stack>
			{props.trailing}
		</HStack>
	);
};
